<template>
  <div :class="clsx(`calendar-grid calendar-grid--${size}`, { 'calendar-disabled': disableCalendar })">
    <div class="day-headers">
      <div
        class="day-header"
        v-for="(day, index) in dayHeaders"
        :key="day"
        :class="clsx({ 'weekend-header': isWeekendHeader(index) })"
      >
        {{ day }}
      </div>
    </div>
    <div class="calendar-days">
      <div
        v-for="date in calendarDates"
        :key="date.key"
        :class="clsx('calendar-day', {
            'other-month': !date.isCurrentMonth,
            today: date.isToday,
            selected: date.isSelected,
            'in-range': range && date.inRange,
            'range-start': range && date.isRangeStart,
            'range-end': range && date.isRangeEnd,
            'is-holiday': date.isHoliday,
            'is-weekend': date.isWeekend,
            'is-disabled': disableCalendar ? true : date.isDisabled
          })"
      >
        <button @click="selectDate(date)">{{ date.day }}
          <span v-if="date.hasEvents" class="event-dot"></span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import clsx from 'clsx';

export default {
  props: {
    /**
     * If true, the entire calendar grid is disabled.
     * @type {boolean}
     */
    disableCalendar: {
      type: Boolean,
      default: false,
    },
    /**
     * Defines the size of the calendar grid.
     * @type {string}
     * @values 'normal', 'small'
     */
    size: {
      type: String,
      default: 'normal',
      validator: (value) => ['normal', 'small'].includes(value)
    },
    /**
     * An array of strings representing the headers for each day of the week.
     * (e.g., ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Нд'])
     * @type {Array<string>}
     * @required
     */
    dayHeaders: {
      type: Array,
      required: true,
    },
    /**
     * An array of date objects, each containing properties for rendering a calendar day.
     * These objects are typically generated by a parent component (e.g., calendar.js).
     * @type {Array<object>}
     * @required
     */
    calendarDates: {
      type: Array,
      required: true,
    },
    /**
     * If true, the calendar is in range selection mode, affecting how dates are styled.
     * @type {boolean}
     */
    range: {
      type: Boolean,
      default: false,
    },
    /**
     * The maximum number of days allowed in a selected range (inclusive).
     * This prop is passed down from calendar-date.vue.
     * @type {number | null}
     */
    maxRangeDays: {
      type: Number,
      default: null,
    },
    /**
     * The start date of a selected range (Moment.js object).
     * This prop is passed down from calendar-date.vue.
     * @type {object}
     */
    startDate: {
      type: Object,
      default: null,
    },
    /**
     * Internal state indicating if a range selection is currently in progress.
     * This prop is passed down from calendar-date.vue.
     * @type {boolean}
     */
    selectingRange: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    clsx,
    /**
     * Emits a 'selectDate' event with the selected date object.
     * @param {object} date - The date object that was clicked.
     */
    selectDate(date) {
      if (this.disableCalendar) {
        return;
      }

      this.$emit('selectDate', date);
    },
    /**
     * Checks if a given day header index corresponds to a weekend day.
     * Assumes Monday is index 0, Saturday is 5, and Sunday is 6.
     * @param {number} index - The index of the day header.
     * @returns {boolean} True if it's a weekend header, false otherwise.
     */
    isWeekendHeader(index) {
      return index === 5 || index === 6; // 5 for Saturday, 6 for Sunday
    },
  },
};
</script>

<style lang="scss" scoped>
$small-scale-factor: 0.8;
$day-header-font-size: 18px;
$day-header-padding: 12px;
$day-button-width: 48px;
$day-button-height: 48px;
$calendar-day-font-size: 20px;

.calendar-grid {
  .day-headers,
  .calendar-days {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
  }

  .day-headers {
    border-bottom: 1px solid #f5f5f5;
  }

  .day-header {
    text-align: center;
    font-family: inherit;
    font-weight: 600;
    font-size: $day-header-font-size;
    padding: $day-header-padding;

    &.weekend-header {
      color: #FF3B30;
    }
  }

  .calendar-day {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    font-family: inherit;
    font-weight: 400;
    font-size: $calendar-day-font-size;
    cursor: pointer;
    transition: all 0.2s ease-in-out;

    button {
      position: relative;
      background: none;
      border: none;
      width: $day-button-width;
      height: $day-button-height;
      transition: background-color 0.2s ease, transform 0.1s ease;
    }

    &:hover {
      button {
        background-color: #EDEDED;
      }
    }

    &:active {
      button {
        transform: scale(0.95);
      }
    }

    &.other-month {
      color: transparent;
      cursor: default;

      button {
        cursor: default;
      }

      &:hover {
        button {
          background-color: transparent;
        }
      }

      &.is-weekend,
      &.is-holiday {
        color: transparent;
      }

      .event-dot {
        display: none;
      }
    }

    &.today {
      button {
        background-color: #EDEDED;
        border-radius: 50%;
      }

      &.other-month {
        button {
          background-color: transparent;
        }
      }
    }

    &.selected {
      button {
        background-color: #EDEDED;
      }

      &.other-month {
        button {
          background-color: transparent;
          color: transparent;
        }
      }

      &.today {
        button {
          border-radius: 0;
        }
      }
    }

    &.in-range {
      button {
        background-color: #EDEDED;
      }

      &.other-month {
        button {
          background-color: transparent;
          color: transparent;
        }
      }

      &.today {
        button {
          border-radius: 0;
        }
      }
    }

    &.range-start {
      button {
        background-color: #EDEDED;
      }
    }

    &.range-end {
      button {
        background-color: #EDEDED;
      }
    }

    .event-dot {
      position: absolute;
      top: 4px;
      right: 4px;
      width: 8px;
      height: 8px;
      background-color: #FF3B30;
      border-radius: 50%;
      margin-top: 4px;
    }

    &.is-holiday {
      color: #FF3B30;
    }

    &.is-weekend {
      color: #FF3B30;
    }

    &.is-disabled {
      cursor: not-allowed;
      pointer-events: none;
      opacity: 0.3;
      color: #999;

      &.other-month {
        background-color: transparent;
        color: transparent;
        opacity: 1;
      }

      button {
        cursor: not-allowed;
        opacity: 1;
        background-color: transparent;
      }

      &.selected,
      &.in-range,
      &.range-start,
      &.range-end {
        button {
          background-color: transparent;
        }
      }

      &:hover {
        button {
          background-color: transparent;
        }
      }
    }
  }

  &--small {
    .day-header {
      font-size: $day-header-font-size * $small-scale-factor;
      padding: $day-header-padding * $small-scale-factor;
    }

    .calendar-day {
      font-size: $calendar-day-font-size * $small-scale-factor;

      button {
        width: $day-button-width * $small-scale-factor;
        height: $day-button-height * $small-scale-factor;
      }
    }
  }

  &.calendar-disabled {
    .day-header {
      color: #808080;
      cursor: not-allowed;
      pointer-events: none;
    }
  }
}
</style>
